import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'
import HistoryIcon from '@mui/icons-material/History'
import GridViewIcon from '@mui/icons-material/GridView'

const matches = (item, query) =>
  `${item.label} ${item.description || ''} ${item.keywords || ''}`
    .toLowerCase()
    .includes(query)

// Sectioned settings navigation: a search box that filters pages across every
// group, group headers with icons, and pill-styled links.
const SettingsMenu = ({ groups, isSiteAdmin, isMobile, setExpandMenu }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const visibleGroups = useMemo(
    () =>
      query
        ? groups
            .map((group) => ({
              ...group,
              items: group.items.filter((item) => matches(item, query)),
            }))
            .filter((group) => group.items.length)
        : groups,
    [groups, query],
  )

  const go = (slug) => {
    navigate(`/site-configuration/${slug}`)
    if (isMobile) setExpandMenu(false)
  }

  const isSelected = (slug) =>
    slug === ''
      ? location.pathname.replace(/\/$/, '') === '/site-configuration'
      : location.pathname.startsWith(`/site-configuration/${slug}`)

  const pillSx = {
    borderRadius: 2,
    py: 0.6,
    px: 1.25,
    '&.Mui-selected': {
      bgcolor: 'anGreen.main',
      color: 'anGreen.contrastText',
      '&:hover': { bgcolor: 'anGreen.main' },
      '& .MuiListItemIcon-root': { color: 'anGreen.contrastText' },
    },
  }

  return (
    <Stack
      gap={1}
      sx={{
        minWidth: '15rem',
        p: 1.25,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        bgcolor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Search settings…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <List dense disablePadding>
        <ListItemButton
          selected={isSelected('')}
          onClick={() => go('')}
          sx={pillSx}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            <GridViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Overview"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: isSelected('') ? 'white' : '',
            }}
            color="white"
          />
        </ListItemButton>
      </List>

      {visibleGroups.map((group) => {
        const GroupIcon = group.icon
        return (
          <Box key={group.name}>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ px: 1, pt: 0.75, pb: 0.25 }}
            >
              <GroupIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: '0.08em', lineHeight: 2 }}
              >
                {group.name}
              </Typography>
            </Stack>
            <List dense disablePadding>
              {group.items.map((item) => (
                <ListItemButton
                  key={item.slug}
                  selected={isSelected(item.slug)}
                  onClick={() => go(item.slug)}
                  sx={pillSx}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isSelected(item.slug) ? 700 : 500,
                      color: isSelected(item.slug) ? 'white' : ''
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )
      })}

      {query && visibleGroups.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 1, py: 1 }}
        >
          No settings match “{search.trim()}”.
        </Typography>
      )}

      {isSiteAdmin && !query && (
        <List dense disablePadding>
          <ListItemButton
            selected={isSelected('activity-log')}
            onClick={() => go('activity-log')}
            sx={pillSx}
          >
            <ListItemIcon sx={{ minWidth: 30 }}>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Activity Log"
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
            />
          </ListItemButton>
        </List>
      )}
    </Stack>
  )
}

export default SettingsMenu
